import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

/**
 * Universal Draft Management Hook
 * 
 * @param {Object} config - Configuration object
 * @param {string} config.featureType - Type of feature (e.g., 'DeliveryOrder', 'ProformaInvoice', 'itemManagement')
 * @param {Object} config.formData - Current form data
 * @param {Function} config.setFormData - Function to update form data
 * @param {string} config.userId - Current user ID
 * @param {string} config.companyId - Current company ID
 * @param {boolean} config.autoDraft - Enable auto-save (default: from employee preferences)
 * @param {number} config.autoDraftInterval - Auto-save interval in ms (default: from employee preferences)
 * @param {Function} config.prepareFormData - Optional async function to prepare form data before saving (e.g., convert images to data URLs)
 * @param {Function} config.onDraftLoaded - Callback when draft is loaded
 * @param {Function} config.onDraftSaved - Callback when draft is saved (receives draft object and isManual boolean)
 * @param {Function} config.onError - Error callback
 * 
 * @returns {Object} Draft management functions and state
 */
const useDraftManager = ({
  featureType,
  formData,
  setFormData,
  userId,
  companyId,
  autoDraft = null, // null means use employee preferences
  autoDraftInterval = null, // null means use employee preferences
  prepareFormData = null, // Optional function to prepare form data before saving
  onDraftLoaded = null,
  onDraftSaved = null,
  onError = null
}) => {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [autoSaving, setAutoSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [draftSettings, setDraftSettings] = useState(null);
  const autoSaveTimerRef = useRef(null);
  const lastSavedDataRef = useRef(null);
  const initializedRef = useRef(false); // Prevent multiple initializations
  const isLoadingDraftRef = useRef(false); // Prevent concurrent loads
  const draftExistsRef = useRef(false); // Track if draft has been created
  const currentSessionDraftIdRef = useRef(null); // Track draft created in current session

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

  // Fetch employee draft settings
  useEffect(() => {
    const fetchSettings = async () => {
      if (!userId) return;

      try {
        const response = await axios.get(`${API_BASE_URL}/employees/draft-settings/${userId}`);
        
        if (response.data.success) {
          const settings = response.data.draftSettings;
          setDraftSettings(settings);

          console.log(`📋 Draft settings loaded for ${featureType}:`, settings);
        }
      } catch (error) {
        console.error('Error fetching draft settings:', error);
      }
    };

    fetchSettings();
  }, [userId, featureType, API_BASE_URL]);

  // Check if form data has meaningful content
  const hasContent = useCallback(() => {
    if (!formData) {
      console.log('📝 hasContent: false - no formData');
      return false;
    }

    let hasAnyData = false;

    // Feature-specific content checks
    if (featureType === 'itemManagement') {
      // For Item Management: check if brand, category, SKU code, or image is filled
      const hasBrand = formData.brand && formData.brand !== '';
      const hasCategory = formData.category && formData.category !== '';
      const hasSkuCode = formData.skuCode && formData.skuCode.trim().length > 0;
      const hasSkuDescription = formData.skuDescription && formData.skuDescription.trim().length > 0;
      const hasHsnCode = formData.hsnCode && formData.hsnCode.trim().length > 0;
      const hasMrp = formData.mrp && formData.mrp !== '';
      const hasImage = formData.image && (
        formData.image instanceof File || 
        (typeof formData.image === 'string' && formData.image.length > 0)
      );
      
      hasAnyData = hasBrand || hasCategory || hasSkuCode || hasSkuDescription || hasHsnCode || hasMrp || hasImage;

      console.log('📝 hasContent check (itemManagement):', { 
        hasBrand, 
        hasCategory,
        hasSkuCode,
        hasSkuDescription,
        hasHsnCode,
        hasMrp,
        hasImage,
        imageType: formData.image ? typeof formData.image : 'none',
        hasAnyData 
      });
    } else if (featureType === 'customerManagement') {
      // For Customer Management: check if customer name or contact info is filled
      const hasCustomerName = formData.name && formData.name.trim().length > 0;
      const hasContactPerson = formData.contactPerson && formData.contactPerson.trim().length > 0;
      const hasPhone = formData.phoneNumber && formData.phoneNumber.trim().length > 0;
      const hasEmail = formData.email && formData.email.trim().length > 0;
      const hasAddress = formData.siteAddress && formData.siteAddress.trim().length > 0;
      const hasGst = formData.gstNo && formData.gstNo.trim().length > 0;
      const hasPan = formData.panNo && formData.panNo.trim().length > 0;

      hasAnyData = hasCustomerName || hasContactPerson || hasPhone || hasEmail || hasAddress || hasGst || hasPan;

      console.log('📝 hasContent check (customerManagement):', {
        hasCustomerName,
        hasContactPerson,
        hasPhone,
        hasEmail,
        hasAddress,
        hasGst,
        hasPan,
        hasAnyData
      });
    } else if (featureType === 'dealerManagement') {
      // For Dealer Management: check if dealer/business info is filled
      const hasBusinessName = formData.businessName && formData.businessName.trim().length > 0;
      const hasBusinessOwner = formData.businessOwner && formData.businessOwner.trim().length > 0;
      const hasPhone = formData.phone && formData.phone.trim().length > 0;
      const hasEmail = formData.email && formData.email.trim().length > 0;
      const hasAddress = formData.address && formData.address.trim().length > 0;
      const hasGst = formData.gstNo && formData.gstNo.trim().length > 0;
      const hasPan = formData.panNo && formData.panNo.trim().length > 0;

      hasAnyData = hasBusinessName || hasBusinessOwner || hasPhone || hasEmail || hasAddress || hasGst || hasPan;

      console.log('📝 hasContent check (dealerManagement):', {
        hasBusinessName,
        hasBusinessOwner,
        hasPhone,
        hasEmail,
        hasAddress,
        hasGst,
        hasPan,
        hasAnyData
      });
    } else if (featureType === 'purchaseIndent') {
      // For Purchase Indent: content if a brand is chosen, items were fetched, or remarks added
      const hasBrand = !!(formData.brand && (formData.brand.value || formData.brand._id));
      const hasItems = (formData.mandatoryItems?.length || 0) > 0 || (formData.additionalItems?.length || 0) > 0;
      const hasRemarks = !!(formData.remarks && formData.remarks.trim().length > 0);

      hasAnyData = hasBrand || hasItems || hasRemarks;
    } else if (featureType && featureType.startsWith('masterSelection')) {
      // For MasterSelection create forms: any non-empty string field counts as content
      hasAnyData = Object.values(formData).some(
        (val) => typeof val === 'string' && val.trim().length > 0
      );
    } else {
      // For Delivery Order and other features: check for items, recipient, etc.
      const hasItems = formData.items && formData.items.some(item => item.itemId && item.itemId !== '');
      const hasRecipient = formData.recipient && formData.recipient.value;
      const hasDeliveryAddress = formData.deliveryAddress && formData.deliveryAddress.trim().length > 0;
      const hasRemarks = formData.remarks && formData.remarks.trim().length > 0;
      const hasCharges = (formData.packingAndForwarding || 0) > 0 || 
                          (formData.labour || 0) > 0 || 
                          (formData.cartage || 0) > 0 ||
                          (formData.discount || 0) > 0;
      
      hasAnyData = hasItems || hasRecipient || hasDeliveryAddress || hasRemarks || hasCharges;

      console.log('📝 hasContent check (delivery/other):', { 
        hasItems, 
        hasRecipient,
        hasDeliveryAddress,
        hasRemarks,
        hasCharges,
        itemsLength: formData.items?.length,
        recipientValue: formData.recipient?.value,
        hasAnyData 
      });
    }

    return hasAnyData;
  }, [formData, featureType]);

  // Check if form data has changed since last save
  const hasChangedSinceLastSave = useCallback(async () => {
    if (!lastSavedDataRef.current) return true;
    
    try {
      // Prepare current form data for comparison (handle images)
      let currentData = formData;
      if (prepareFormData && typeof prepareFormData === 'function') {
        currentData = await prepareFormData(formData);
      }
      
      const currentDataString = JSON.stringify(currentData);
      const lastSavedDataString = JSON.stringify(lastSavedDataRef.current);
      
      return currentDataString !== lastSavedDataString;
    } catch (error) {
      console.error('Error comparing form data changes:', error);
      // If comparison fails, assume data has changed
      return true;
    }
  }, [formData, prepareFormData]);

  // Fetch all drafts for this user and feature (only one draft per feature)
  const fetchDrafts = useCallback(async (autoLoad = false) => {
    if (!userId || !featureType) return;

    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/drafts`, {
        params: {
          userId,
          companyId,
          featureType
        }
      });

      if (response.data.success) {
        const fetchedDrafts = response.data.drafts;
        
        // Filter out the current session draft (don't show it in the list if user hasn't navigated away)
        const filteredDrafts = fetchedDrafts.filter(draft => 
          draft._id !== currentSessionDraftIdRef.current
        );
        
        setDrafts(filteredDrafts);
        
        // Auto-load the draft if requested (since there's only one)
        if (autoLoad && fetchedDrafts.length > 0) {
          const draft = fetchedDrafts[0];
          console.log('📥 Auto-loading draft:', draft._id);
          setFormData(prevForm => ({
            ...prevForm,          // Preserve existing form structure
            ...draft.draftData    // Merge with draft data
          }));
          lastSavedDataRef.current = JSON.parse(JSON.stringify(draft.draftData));
          
          if (onDraftLoaded) {
            onDraftLoaded(draft);
          }
        }
        
        return fetchedDrafts.length > 0 ? fetchedDrafts[0] : null;
      }
    } catch (error) {
      console.error('Error fetching drafts:', error);
      if (onError) onError(error);
    } finally {
      setLoading(false);
    }
    
    return null;
  }, [userId, companyId, featureType, API_BASE_URL, setFormData, onDraftLoaded, onError]);

  // Save draft (auto or manual)
  const saveDraft = useCallback(async (isManual = false, draftName = '', description = '') => {
    console.log('🔵 saveDraft called:', { isManual, userId, companyId, featureType, hasFormData: !!formData });
    
    if (!userId || !companyId || !featureType || !formData) {
      console.warn('❌ Missing required data for draft save:', { userId: !!userId, companyId: !!companyId, featureType, formData: !!formData });
      return null;
    }

    // Don't save if no meaningful content
    if (!hasContent()) {
      console.log('⚠️ No content to save in draft');
      return null;
    }

    // Don't auto-save if data hasn't changed
    if (!isManual && !(await hasChangedSinceLastSave())) {
      console.log('⚠️ No changes detected, skipping auto-save');
      return null;
    }

    try {
      if (!isManual) setAutoSaving(true);

      // Prepare form data for saving (handle images, etc.)
      let draftData = formData;
      if (prepareFormData && typeof prepareFormData === 'function') {
        try {
          draftData = await prepareFormData(formData);
          console.log('📤 Form data prepared for draft saving');
        } catch (error) {
          console.error('Error preparing form data for draft:', error);
          // Continue with original data if preparation fails
        }
      }

      const payload = {
        userId,
        companyId,
        featureType,
        draftData: draftData,
        isAutoSaved: !isManual,
        draftName: draftName || `${featureType} Draft - ${new Date().toLocaleString('en-IN')}`,
        description
      };

      console.log('📤 Sending draft to API:', { 
        userId, 
        companyId, 
        featureType, 
        isAutoSaved: !isManual,
        draftName: payload.draftName 
      });

      const response = await axios.post(`${API_BASE_URL}/drafts`, payload);

      if (response.data.success) {
        console.log('✅ Draft saved successfully:', response.data.draft._id);
        setLastSaved(new Date());
        lastSavedDataRef.current = JSON.parse(JSON.stringify(draftData)); // Store prepared data for comparison
        draftExistsRef.current = true; // Mark that draft has been created
        currentSessionDraftIdRef.current = response.data.draft._id; // Track current session draft
        
        // Update form state with prepared data to prevent continuous re-saves
        // This is important when images are converted from File to data URL
        if (prepareFormData && draftData !== formData) {
          console.log('🔄 Updating form state with prepared draft data');
          setFormData(prevForm => ({
            ...prevForm,
            ...draftData
          }));
        }
        
        if (onDraftSaved) {
          onDraftSaved(response.data.draft, isManual);
        }

        // Refresh drafts list if manual save
        if (isManual) {
          await fetchDrafts();
        }

        return response.data.draft;
      }
    } catch (error) {
      console.error('❌ Error saving draft:', error);
      console.error('Error details:', error.response?.data || error.message);
      if (onError) onError(error);
    } finally {
      if (!isManual) setAutoSaving(false);
    }

    return null;
  }, [userId, companyId, featureType, formData, hasContent, hasChangedSinceLastSave, prepareFormData, setFormData, API_BASE_URL, onDraftSaved, onError, fetchDrafts]);

  // Load draft (simplified - just loads the one draft that exists)
  const loadDraft = useCallback(async (draftId = null) => {
    try {
      setLoading(true);
      
      // If no draftId provided, fetch and load the single draft for this feature
      if (!draftId) {
        const draft = await fetchDrafts(true); // Auto-load
        return draft;
      }
      
      // Load specific draft by ID
      const response = await axios.get(`${API_BASE_URL}/drafts/${draftId}`);

      if (response.data.success) {
        const draft = response.data.draft;
        console.log('📥 Loading draft:', draft._id);
        setFormData(prevForm => ({
          ...prevForm,           // Preserve existing form structure
          ...draft.draftData     // Merge with draft data
        }));
        lastSavedDataRef.current = JSON.parse(JSON.stringify(draft.draftData));
        currentSessionDraftIdRef.current = draft._id; // Track as current session draft
        draftExistsRef.current = true; // Mark that draft exists
        
        if (onDraftLoaded) {
          onDraftLoaded(draft);
        }

        return draft;
      }
    } catch (error) {
      console.error('Error loading draft:', error);
      if (onError) onError(error);
    } finally {
      setLoading(false);
    }

    return null;
  }, [API_BASE_URL, setFormData, onDraftLoaded, onError, fetchDrafts]);

  // Delete draft
  const deleteDraft = useCallback(async (draftId) => {
    try {
      const response = await axios.delete(`${API_BASE_URL}/drafts/${draftId}`);

      if (response.data.success) {
        // Clear current session draft reference if this draft was being worked on
        if (currentSessionDraftIdRef.current === draftId) {
          currentSessionDraftIdRef.current = null;
          draftExistsRef.current = false;
        }
        await fetchDrafts();
        return true;
      }
    } catch (error) {
      console.error('Error deleting draft:', error);
      if (onError) onError(error);
    }

    return false;
  }, [API_BASE_URL, fetchDrafts, onError]);

  // Delete all auto-saved drafts
  const clearAutoSavedDrafts = useCallback(async () => {
    if (!userId || !featureType) return false;

    try {
      const response = await axios.delete(
        `${API_BASE_URL}/drafts/auto-saved/${userId}/${featureType}`,
        { params: { companyId } }
      );

      if (response.data.success) {
        await fetchDrafts();
        return true;
      }
    } catch (error) {
      console.error('Error clearing auto-saved drafts:', error);
      if (onError) onError(error);
    }

    return false;
  }, [userId, companyId, featureType, API_BASE_URL, fetchDrafts, onError]);

  // Get latest auto-saved draft
  const getLatestAutoSavedDraft = useCallback(() => {
    const autoSavedDrafts = drafts.filter(d => d.isAutoSaved);
    if (autoSavedDrafts.length === 0) return null;
    
    return autoSavedDrafts.reduce((latest, current) => {
      return new Date(current.updatedAt) > new Date(latest.updatedAt) ? current : latest;
    });
  }, [drafts]);

  // Auto-save effect
  useEffect(() => {
    // Default to enabled if settings not loaded yet (since default in backend is true)
    const isDraftEnabled = draftSettings === null ? true : draftSettings?.draftEnabled;
    
    console.log('🔄 Auto-save effect triggered:', { 
      draftEnabled: isDraftEnabled, 
      userId: !!userId, 
      companyId: !!companyId, 
      featureType,
      autoDraft,
      draftExists: draftExistsRef.current,
      interval: autoDraftInterval || 5000,
      draftSettingsLoaded: draftSettings !== null
    });

    // Check if drafts are enabled and we have required data
    if (!isDraftEnabled || !userId || !companyId || !featureType) {
      console.log('⚠️ Auto-save disabled: missing requirements');
      return;
    }

    // Only auto-save if autoDraft prop is true (or not provided = default true)
    const shouldAutoSave = autoDraft !== false;
    if (!shouldAutoSave) {
      console.log('⚠️ Auto-save disabled by prop');
      return;
    }

    // Don't auto-save if draft hasn't been created yet
    if (!draftExistsRef.current) {
      console.log('⚠️ Auto-save skipped: draft not created yet');
      return;
    }

    // Check if form has any content using the feature-aware hasContent function
    if (!hasContent()) {
      console.log('⚠️ Auto-save skipped: no content');
      return;
    }

    console.log('✅ Setting up auto-save timer...');

    // Clear existing timer
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    // Set new timer (5 seconds as specified)
    const interval = autoDraftInterval || 5000;
    autoSaveTimerRef.current = setTimeout(async () => {
      console.log('⏰ Auto-save timer triggered');
      const hasChanged = await hasChangedSinceLastSave();
      if (hasChanged) {
        console.log('💾 Calling saveDraft...');
        saveDraft(false);
      } else {
        console.log('⚠️ No changes since last save');
      }
    }, interval);

    console.log(`⏱️ Auto-save timer set for ${interval}ms`);

    // Cleanup
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [draftSettings, autoDraft, autoDraftInterval, userId, companyId, featureType, formData, saveDraft, hasContent, hasChangedSinceLastSave]);

  // Auto-create draft on first meaningful input
  useEffect(() => {
    // Default to enabled if settings not loaded yet (since default in backend is true)
    const isDraftEnabled = draftSettings === null ? true : draftSettings?.draftEnabled;
    
    // Skip if not initialized yet
    if (!initializedRef.current) {
      console.log('⚠️ Auto-create: Not initialized yet');
      return;
    }

    // Skip if draft already exists
    if (draftExistsRef.current) {
      console.log('⚠️ Auto-create: Draft already exists');
      return;
    }

    // Skip if missing requirements
    if (!isDraftEnabled || !userId || !companyId || !featureType) {
      console.log('⚠️ Auto-create: Missing requirements', { isDraftEnabled, userId: !!userId, companyId: !!companyId, featureType });
      return;
    }

    // Check if form has any content using the feature-aware hasContent function
    const formHasContent = hasContent();
    
    console.log('🔄 Auto-create effect:', { 
      initialized: initializedRef.current,
      draftExists: draftExistsRef.current,
      formHasContent,
      draftEnabled: isDraftEnabled,
      featureType
    });

    // Create draft when user makes first meaningful input
    if (formHasContent) {
      console.log('✅ First input detected! Creating draft...');
      saveDraft(false); // Auto-save (not manual)
    }
  }, [formData, draftSettings, userId, companyId, featureType, saveDraft, hasContent]);

  // Auto-delete draft when form becomes empty
  useEffect(() => {
    // Default to enabled if settings not loaded yet (since default in backend is true)
    const isDraftEnabled = draftSettings === null ? true : draftSettings?.draftEnabled;

    // Only proceed if initialized and draft exists
    if (!initializedRef.current) {
      return;
    }

    if (!draftExistsRef.current || !currentSessionDraftIdRef.current) {
      return;
    }

    // Check if drafts are enabled and we have required data
    if (!isDraftEnabled || !userId || !companyId || !featureType) {
      return;
    }

    // Check if form has any content using the feature-aware hasContent function
    const formHasContent = hasContent();

    console.log('🔄 Auto-delete effect:', { 
      initialized: initializedRef.current,
      draftExists: draftExistsRef.current,
      formHasContent,
      currentDraftId: currentSessionDraftIdRef.current,
      featureType
    });

    // Delete draft when form becomes completely empty
    if (!formHasContent) {
      console.log('🗑️ Form is empty! Deleting draft...');
      deleteDraft(currentSessionDraftIdRef.current);
    }
  }, [formData, draftSettings, userId, companyId, featureType, deleteDraft, hasContent]);

  // Check for existing draft on initialization (don't create empty draft)
  const checkExistingDraft = useCallback(async () => {
    // Prevent multiple initializations
    if (isLoadingDraftRef.current || initializedRef.current) {
      console.log('⚠️ Already initialized or currently loading, skipping...');
      return null;
    }

    if (!userId || !companyId || !featureType || !draftSettings?.draftEnabled) {
      return null;
    }

    isLoadingDraftRef.current = true;
    console.log('🔍 Checking for existing draft...');
    
    try {
      // Check if any draft exists
      const response = await axios.get(`${API_BASE_URL}/drafts`, {
        params: {
          userId,
          companyId,
          featureType
        }
      });

      if (response.data.success && response.data.count > 0) {
        console.log('✅ Existing draft found:', response.data.draft._id);
        const existingDraft = response.data.draft;
        
        // Use draft data as-is, don't add default fields that might not exist for this feature type
        const safeData = existingDraft.draftData;
        
        // Auto-load the existing draft into the form
        console.log('📥 Auto-loading existing draft into form...', { featureType, draftData: safeData });
        setFormData(prevForm => ({
          ...prevForm, // Preserve existing form structure
          ...safeData   // Merge with draft data
        }));
        lastSavedDataRef.current = JSON.parse(JSON.stringify(safeData));
        setDrafts([]); // Don't show in drafts list since we're loading it in current session
        draftExistsRef.current = true; // Mark that draft exists
        currentSessionDraftIdRef.current = existingDraft._id; // Track as current session draft
        initializedRef.current = true; // Mark as initialized
        
        // Call onDraftLoaded WITHOUT triggering re-initialization
        if (onDraftLoaded) {
          // Use setTimeout to break the execution chain
          setTimeout(() => {
            onDraftLoaded(existingDraft);
          }, 0);
        }
        
        return existingDraft;
      }

      // No draft exists - don't create empty draft, wait for user to make changes
      console.log('ℹ️ No existing draft found. Will create on first save.');
      initializedRef.current = true; // Mark as initialized
      draftExistsRef.current = false; // No draft yet
      
      return null;
    } catch (error) {
      console.error('❌ Error checking for existing draft:', error);
      if (onError) onError(error);
    } finally {
      isLoadingDraftRef.current = false;
    }

    return null;
  }, [userId, companyId, featureType, draftSettings, API_BASE_URL, setFormData, onError, onDraftLoaded]);

  // Check for existing drafts on mount (ONLY ONCE)
  useEffect(() => {
    const initializeDrafts = async () => {
      // Only initialize once when all required data is available
      if (initializedRef.current) {
        console.log('⚠️ Already initialized, skipping');
        return;
      }

      // Wait for userId, featureType, AND draftSettings to be loaded
      if (!userId || !featureType || draftSettings === null) {
        console.log('⏳ Waiting for required data...', { userId: !!userId, featureType: !!featureType, draftSettingsLoaded: draftSettings !== null });
        return;
      }

      console.log('🚀 Initializing drafts for', featureType);
      
      // If draftSettings loaded and enabled, check for existing draft
      if (draftSettings?.draftEnabled) {
        console.log('📋 Draft settings enabled, checking for existing drafts...');
        await checkExistingDraft();
      } else {
        // Settings loaded but drafts disabled
        console.log('🚫 Draft settings disabled');
        initializedRef.current = true;
      }
    };

    initializeDrafts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, featureType, draftSettings]); // Re-runs when any of these change

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
      // Reset initialization flag on unmount
      initializedRef.current = false;
      isLoadingDraftRef.current = false;
      // Clear current session draft ID so it can appear in drafts list on next visit
      currentSessionDraftIdRef.current = null;
    };
  }, []);

  return {
    // State
    drafts, // Will always be array with 0 or 1 item
    draft: drafts.length > 0 ? drafts[0] : null, // Convenience property for the single draft
    loading,
    autoSaving,
    lastSaved,
    draftSettings,
    hasDraft: drafts.length > 0,
    currentDraftId: currentSessionDraftIdRef.current, // Current session draft ID
    
    // Functions
    fetchDrafts,
    saveDraft,
    loadDraft,
    deleteDraft,
    clearAutoSavedDrafts,
    getLatestAutoSavedDraft,
    checkExistingDraft,
    hasContent,
    
    // Utilities (kept for backward compatibility but simplified)
    manualSaveDrafts: drafts.filter(d => !d.isAutoSaved),
    autoSavedDrafts: drafts.filter(d => d.isAutoSaved)
  };
};

export default useDraftManager;
